import { useState } from "react";
import { defaultProfileImage } from "../../../../../../utils/constants";
import { handleImageErrorProfile } from "../../../../../../utils/UtilsFiles";
import { sanitizeHtml } from "../../../../../../helpers/sanitizeHtml";
import { toAbsoluteUrl } from "../../../../../../../_metronic/helpers";

type Props = {
    conversation: any;
}

export const MessagesMastermindCircleInfo = ({ conversation }: Props) => {
    const [open, setOpen] = useState(true); 
    const [isExpanded, setIsExpanded] = useState(false);

    const author = conversation.author;
    const facilitator = conversation.facilitator;
    const memberCount = conversation.memberCount || conversation.member_count || 0;
    const description = conversation.description || "";

    // Safely count characters in HTML by parsing plain text
    const plainText = description.replace(/<[^>]*>/g, "");
    const needsTruncation = plainText.length > 300;

    // Helper to safely truncate HTML without breaking elements
    const truncateHtml = (html: string, limit: number): string => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            let count = 0;
            
            const recurse = (node: Node): boolean => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const text = node.textContent || "";
                    if (count + text.length > limit) {
                        node.textContent = text.substring(0, limit - count) + "...";
                        count = limit;
                        return true;
                    }
                    count += text.length;
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    const childNodes = Array.from(node.childNodes);
                    for (const child of childNodes) {
                        const stop = recurse(child);
                        if (stop) {
                            while (child.nextSibling) {
                                node.removeChild(child.nextSibling);
                            }
                            return true;
                        }
                    }
                }
                return false;
            };
            
            recurse(doc.body);
            return doc.body.innerHTML;
        } catch (e) {
            return html.substring(0, limit) + "...";
        }
    };

    const displayDescription = (!isExpanded && needsTruncation) 
        ? truncateHtml(description, 300) 
        : description;

    const chatType = conversation.chatType || conversation.chat_type || "";
    const isForum = chatType === "forum";

    return (
        <div className="messages-info-card">
            <div className="d-flex flex-row justify-content-between w-100" style={{ cursor: 'pointer' }} onClick={() => setOpen(prev => !prev)}>
                <span className="title">{isForum ? "Community Info" : "Mastermind Circle Info"}</span>
                <div className="collapse-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                        <path d="M12.5194 9.70701C12.4759 9.75059 12.4242 9.78517 12.3673 9.80876C12.3104 9.83235 12.2494 9.84449 12.1878 9.84449C12.1262 9.84449 12.0652 9.83235 12.0083 9.80876C11.9514 9.78517 11.8997 9.75059 11.8561 9.70701L7.50026 5.35056L3.1444 9.70701C3.05644 9.79497 2.93715 9.84438 2.81276 9.84438C2.68837 9.84438 2.56908 9.79497 2.48112 9.70701C2.39316 9.61905 2.34375 9.49976 2.34375 9.37537C2.34375 9.25098 2.39316 9.13168 2.48112 9.04373L7.16862 4.35623C7.21215 4.31265 7.26385 4.27807 7.32076 4.25448C7.37766 4.23089 7.43866 4.21875 7.50026 4.21875C7.56186 4.21875 7.62286 4.23089 7.67976 4.25448C7.73667 4.27807 7.78837 4.31265 7.8319 4.35623L12.5194 9.04373C12.563 9.08726 12.5976 9.13896 12.6211 9.19587C12.6447 9.25277 12.6569 9.31377 12.6569 9.37537C12.6569 9.43697 12.6447 9.49797 12.6211 9.55487C12.5976 9.61178 12.563 9.66348 12.5194 9.70701Z" fill="#151515"/>
                    </svg>
                </div>
            </div>
            {open && (
                <div className="d-flex flex-column w-100 align-items-start" style={{ gap: '20px' }}>
                    <div className="d-flex flex-column w-100" style={{ gap: '10px' }}>
                        <h6 className="title-2">{conversation.title || "Mastermind Circle"}</h6>
                        {description ? (
                            <div className="d-flex flex-column align-items-start w-100" style={{ gap: '10px' }}>
                                <div 
                                    className="text-body description-html-container w-100" 
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayDescription) }}
                                    style={{ fontSize: '13px', lineHeight: '1.5', whiteSpace: 'normal', wordBreak: 'break-word', color: '#262626' }}
                                />
                                {needsTruncation && (
                                    <button 
                                        className="text-action" 
                                        onClick={() => setIsExpanded(prev => !prev)}
                                        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}
                                    >
                                        {isExpanded ? "READ LESS" : "READ MORE"}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <p className="text-body">
                                No description provided.
                            </p>
                        )}
                    </div>

                    <svg xmlns="http://www.w3.org/2000/svg" width="295" height="1" viewBox="0 0 295 1" fill="none">
                        <path d="M0 0.5H295" stroke="#E9E9E9"/>
                    </svg>

                    <div className="d-grid w-100" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'repeat(2, 1fr)', gap: '20px' }}>
                        {author && (
                            <div className="d-flex flex-column align-items-start" style={{ gap: '5px' }}>
                                <span className="text-muted">
                                    Owned by
                                </span>
                                <div className="d-flex flex-row align-items-center author" style={{ gap: '5px' }}>
                                    <img src={author.avatar ? (author.avatar.startsWith('http') ? author.avatar : toAbsoluteUrl(author.avatar)) : toAbsoluteUrl(defaultProfileImage)} onError={handleImageErrorProfile} />
                                    <span className="text-bold">{(conversation.isHost ?? conversation.is_host) ? "You" : author.name}</span>
                                    <span className="host-tag">
                                        HOST
                                    </span>
                                </div>
                            </div>
                        )}

                        {facilitator ? (
                            <div className="d-flex flex-column align-items-start" style={{ gap: '5px' }}>
                                <span className="text-muted">
                                    Facilitator
                                </span>
                                <div className="d-flex flex-row align-items-center author" style={{ gap: '5px' }}>
                                    <img src={facilitator.avatar ? (facilitator.avatar.startsWith('http') ? facilitator.avatar : toAbsoluteUrl(facilitator.avatar)) : toAbsoluteUrl(defaultProfileImage)} onError={handleImageErrorProfile} />
                                    <span className="text-bold">{facilitator.name}</span>
                                    <span className="host-tag">
                                        team
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex flex-column align-items-start" style={{ gap: '5px' }}>
                                <span className="text-muted">
                                    Facilitator
                                </span>
                                <div className="d-flex flex-row align-items-center author" style={{ gap: '5px' }}>
                                    <span className="text-bold">Nobilis Staff</span>
                                    <span className="host-tag">
                                        team
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="d-flex flex-column align-items-start" style={{ gap: '5px' }}>
                            <span className="text-muted">
                                Members
                            </span>
                            <div className="d-flex flex-row align-items-center author" style={{ gap: '5px' }}>
                                <div className="avatars">
                                    {Array.isArray(conversation.avatar) ? conversation.avatar.slice(0, 4).map((av: string, i: number) => (
                                        <img key={i} src={av.startsWith('http') ? av : toAbsoluteUrl(av)} onError={handleImageErrorProfile} style={{zIndex: 4 - i, position: 'relative'}}/>
                                    )) : (
                                        <img src={toAbsoluteUrl(defaultProfileImage)} style={{zIndex: 4, position: 'relative'}} />
                                    )}
                                </div>
                                {memberCount > 4 && (
                                    <span className="text-body">+{memberCount - 4}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}